import { useState } from 'react';

import { Group, Paper } from '@mantine/core';

import {
  type CompletionRingStatus,
  CompletionRingView,
} from '../../../dashboard/atoms/CompletionRing';
import type { ResourceAvatarVariant } from '../../../gantt/ResourceAvatar';
import { IntervalTagView } from '../IntervalTag';
import { UserCellView } from '../UserCell';

export interface IntervalCompletionRowViewProps {
  intervalId: number | string;
  startDate: string | Date;
  endDate: string | Date;
  intervalColor?: string;
  percentage: number;
  status: CompletionRingStatus;
  userName: string;
  userSubtitle?: string;
  avatarVariant?: ResourceAvatarVariant;
  photoUrl?: string | null;
  ringSize?: number;
  /** Interval tag is the most prominent element in the row — sized up by default. */
  tagSize?: 'xs' | 'sm' | 'md';
  /** Renders as its own bordered card. Set false inside a container that already provides the frame (e.g. CardView). */
  bordered?: boolean;
  /** Bottom divider line, used when bordered=false to separate stacked rows. */
  withDivider?: boolean;
  /** Shows a hover background — set when the row is clickable (e.g. wrapped to open an update popover). */
  interactive?: boolean;
}

export function IntervalCompletionRowView({
  intervalId,
  startDate,
  endDate,
  intervalColor,
  percentage,
  status,
  userName,
  userSubtitle,
  avatarVariant,
  photoUrl,
  ringSize = 36,
  tagSize = 'md',
  bordered = true,
  withDivider = false,
  interactive = false,
}: IntervalCompletionRowViewProps) {
  const [hovered, setHovered] = useState(false);

  const content = (
    <Group justify="space-between" wrap="nowrap" align="center">
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
        <CompletionRingView percentage={percentage} status={status} size={ringSize} />
        <UserCellView
          name={userName}
          subtitle={userSubtitle}
          avatarVariant={avatarVariant}
          photoUrl={photoUrl}
          truncate
        />
      </Group>
      <IntervalTagView
        intervalId={intervalId}
        startDate={startDate}
        endDate={endDate}
        color={intervalColor}
        size={tagSize}
      />
    </Group>
  );

  return bordered ? (
    <Paper withBorder radius="md" p="md">
      {content}
    </Paper>
  ) : (
    <div
      onMouseEnter={interactive ? () => setHovered(true) : undefined}
      onMouseLeave={interactive ? () => setHovered(false) : undefined}
      style={{
        padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-sm)',
        marginInline: 'calc(var(--mantine-spacing-sm) * -1)',
        borderRadius: 'var(--mantine-radius-sm)',
        borderBottom: withDivider ? '1px solid var(--mantine-color-default-border)' : undefined,
        backgroundColor: interactive && hovered ? 'var(--mantine-color-gray-0)' : undefined,
        cursor: interactive ? 'pointer' : undefined,
        transition: 'background-color 120ms',
      }}
    >
      {content}
    </div>
  );
}
